import React, { useEffect, useState } from 'react';
import { useReportContext } from '../../../../_context/ReportContext';
import { Alert } from 'react-bootstrap';
import { SignaturePadComponent } from '../../../../htmlComponents/signaturePadComponent';
import SignatureSavedModal from './CreateReportAlertModals/SignatureSavedConfirmModal';
import { ReportViewModal } from '../../ViewReports/ReportTemplate/ReportViewModal';
import { FaCheckCircle } from 'react-icons/fa';
import "../../../Admin/AddUsers/AddUsers.css";
import "../../Reports.css";

export const ClientAndCNASignatures = () => {
  const { setNewReport, newReport, setCurrentViewingReport } = useReportContext();
  const [clientSignature, setClientSignature] = useState<string>('');
  const [qaSignature, setQaSignature] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showSignatureSavedModal, setShowSignatureSavedModal] = useState(false);
  const [viewReport, setViewReport] = useState<boolean>(false);

  // Initialize local state from context when component mounts
  useEffect(() => {
    if (newReport.ClientSignature) {
      setClientSignature(newReport.ClientSignature);
    }
    if (newReport.QASignature) {
      setQaSignature(newReport.QASignature);
    }
  }, [newReport.ClientSignature, newReport.QASignature]);

  const saveClientSignature = (base64: string) => {
    setClientSignature(base64);
    setShowSignatureSavedModal(true);
  };

  const saveQaSignature = (base64: string) => {
    setQaSignature(base64);
    setShowSignatureSavedModal(true);
  };

  const handleCloseModal = () => setShowSignatureSavedModal(false);

  useEffect(() => {
    if (clientSignature && qaSignature) {
      if (newReport.ClientSignature !== clientSignature || newReport.QASignature !== qaSignature) {
        setNewReport(prev => ({
          ...prev,
          ClientSignature: clientSignature,
          QASignature: qaSignature
        }));
      }
    }
  }, [clientSignature, qaSignature, newReport.ClientSignature, newReport.QASignature, setNewReport]);

  const handlePageSubmit = () => {
    if ((newReport.ClientSignature === "") || newReport.QASignature === "") {
      setErrorMessage("Please make sure you have saved both signatures before proceeding.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setErrorMessage("");
      setViewReport(true);
      setCurrentViewingReport(newReport);
    }
  };

  return (
    <div className="reports-page-wrapper" style={{ maxWidth: 900 }}>
      {/* Header */}
      <div className="rp-client-header" style={{ background: "linear-gradient(135deg, #111827 0%, #374151 100%)" }}>
        <h2 className="rp-client-title">Step 6: Signatures</h2>
        <div className="rp-client-email" style={{ marginTop: 4 }}>
          Final sign-off by Client and Assessor
        </div>
      </div>

      {errorMessage && (
        <div style={{ animation: "auFadeIn 0.3s ease", marginBottom: "20px" }}>
          <Alert variant="danger" style={{ borderRadius: "8px", border: "1px solid #fecaca", fontWeight: 500 }}>
            {errorMessage}
          </Alert>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "24px" }}>

        {/* Client Signature */}
        <div className="au-card">
          <div className="au-card-body" style={{ padding: "24px", display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
              <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f1c34", margin: 0 }}>
                Client Signature
              </h3>
              {newReport.ClientSignature && (
                <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.8rem", color: "#16a34a", fontWeight: 600, background: "rgba(22,163,74,0.1)", padding: "4px 8px", borderRadius: "20px" }}>
                  <FaCheckCircle /> Saved
                </span>
              )}
            </div>
            <div style={{ flex: 1, minHeight: "260px", border: "1px dashed #D1D5DB", borderRadius: "12px", background: "#F9FAFB", padding: "12px", display: "flex", flexDirection: "column" }}>
              <SignaturePadComponent
                saveSignature={saveClientSignature}
                initialSignature={newReport.ClientSignature}
              />
            </div>
          </div>
        </div>

        {/* QA Signature */}
        <div className="au-card">
          <div className="au-card-body" style={{ padding: "24px", display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
              <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f1c34", margin: 0 }}>
                QA Assessor Signature
              </h3>
              {newReport.QASignature && (
                <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.8rem", color: "#16a34a", fontWeight: 600, background: "rgba(22,163,74,0.1)", padding: "4px 8px", borderRadius: "20px" }}>
                  <FaCheckCircle /> Saved
                </span>
              )}
            </div>
            <div style={{ flex: 1, minHeight: "260px", border: "1px dashed #D1D5DB", borderRadius: "12px", background: "#F9FAFB", padding: "12px", display: "flex", flexDirection: "column" }}>
              <SignaturePadComponent
                saveSignature={saveQaSignature}
                initialSignature={newReport.QASignature}
              />
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: "32px", textAlign: "center" }}>
        {(() => {
          const isComplete = newReport.ClientSignature && newReport.QASignature;

          return (
            <button
              className="au-submit-btn hover-trigger"
              onClick={handlePageSubmit}
              disabled={!isComplete}
              style={{
                width: "100%",
                maxWidth: "400px",
                background: isComplete ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" : "#D1D5DB",
                color: isComplete ? "#fff" : "#9CA3AF",
                boxShadow: isComplete ? "0 4px 14px rgba(16,185,129,0.3)" : "none",
                cursor: isComplete ? "pointer" : "not-allowed",
                border: "none",
                transition: "all 0.3s ease"
              }}
            >
              Review Final Report
            </button>
          );
        })()}
      </div>

      <SignatureSavedModal show={showSignatureSavedModal} handleClose={handleCloseModal} />
      <ReportViewModal viewReport={viewReport} setViewReport={setViewReport} />
    </div>
  );
};
